import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MoreHorizontal, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import {
  useVirtualTourLinkedWithoutAssetQuery,
  useVirtualTourRunsQuery,
} from '@/client/api/listing-management/virtual-tours.query';
import {
  useReconcileVirtualToursMutation,
  useResyncListingTourMutation,
} from '@/client/api/listing-management/virtual-tours.mutation';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useQueryClient } from '@tanstack/react-query';
import { virtualTourKeys } from '@/client/api/listing-management/virtual-tours.query';
import { fmtDate, LinkedListing, StatusBadge } from './shared';
import { ReasonDialog } from './ReasonDialog';

const durationOf = (start: string | null, end: string | null): string => {
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

export const SyncTab: React.FC = () => {
  const navigate = useNavigate();
  const { data: runs, isLoading: runsLoading, isError: runsError, error: runsErr } = useVirtualTourRunsQuery();
  const { data: linked, isLoading: linkedLoading } = useVirtualTourLinkedWithoutAssetQuery();
  const qc = useQueryClient();

  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [fullRescan, setFullRescan] = useState(false);
  const reconcile = useReconcileVirtualToursMutation();

  const [retryEid, setRetryEid] = useState<number | null>(null);
  const canRetry = useGate(PERMISSIONS.virtualTour.edit).allowed;
  const resyncListing = useResyncListingTourMutation();

  const confirmReconcile = async (reason: string) => {
    try {
      await reconcile.mutateAsync({ reason, full_rescan: fullRescan });
      toast.success('Reconcile queued.');
      setReconcileOpen(false);
      setFullRescan(false);
      // The run lands asynchronously — give the worker a moment, then refresh.
      setTimeout(() => qc.invalidateQueries({ queryKey: virtualTourKeys.all }), 5000);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to queue the reconcile.'));
    }
  };

  const confirmRetry = async (reason: string) => {
    if (retryEid == null) return;
    try {
      await resyncListing.mutateAsync({ eid: retryEid, reason });
      toast.success('Re-sync queued.');
      setRetryEid(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to re-sync this listing.'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <PermissionButton code={PERMISSIONS.virtualTour.edit} onClick={() => setReconcileOpen(true)}>
          Run reconcile now
        </PermissionButton>
      </div>

      {runsError ? (
        <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-6 text-sm text-red-600">
          {getApiErrorMessage(runsErr, 'Could not load sync runs.')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Started</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Triggered by</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Scanned</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Linked</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Blocked</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Failed</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Repaired</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {runsLoading && !runs
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-4 py-3" colSpan={10}>
                        <Skeleton className="h-5 w-full" />
                      </td>
                    </tr>
                  ))
                : (runs ?? []).map((r) => (
                    <tr key={r.run_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(r.started_at)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.triggered_by || '—'}</td>
                      <td className="px-4 py-3">{r.status ? <StatusBadge status={r.status} /> : '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.scanned ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.linked ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.quota_blocked ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.failed ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.repaired ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{durationOf(r.started_at, r.finished_at)}</td>
                      <td className="px-4 py-3 text-sm text-red-600">{r.error || '—'}</td>
                    </tr>
                  ))}
              {!runsLoading && (runs?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-sm text-gray-500">
                    No reconcile runs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-700">
            Linked but missing tour {linked ? <span className="text-gray-400">({linked.total})</span> : null}
          </h3>
        </div>
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Listing</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">External id</th>
              <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {linkedLoading && !linked ? (
              <tr>
                <td className="px-4 py-3" colSpan={3}>
                  <Skeleton className="h-5 w-full" />
                </td>
              </tr>
            ) : (linked?.listings ?? []).length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-500">
                  None.
                </td>
              </tr>
            ) : (
              (linked?.listings ?? []).map((l) => (
                <tr key={l.eid} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm">
                    <LinkedListing eid={l.eid} name={l.listing_name} onOpen={() => navigate(`/admin/listings/${l.eid}`)} />
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{l.external_id || '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Actions">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem disabled={!canRetry} onClick={() => setRetryEid(l.eid)}>
                          <RefreshCw /> Retry
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ReasonDialog
        open={reconcileOpen}
        title="Run reconcile now?"
        description="Queues a sweep that scans the provider for new/changed tours and links them to listings. The same sweep also runs automatically every 15 minutes."
        busy={reconcile.isPending}
        confirmLabel="Run reconcile"
        onCancel={() => setReconcileOpen(false)}
        onConfirm={confirmReconcile}
      >
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300"
            checked={fullRescan}
            onChange={(e) => setFullRescan(e.target.checked)}
          />
          Full rescan (ignore the last watermark)
        </label>
      </ReasonDialog>

      <ReasonDialog
        open={retryEid != null}
        title="Re-sync this listing?"
        description="Queues a fresh sync from the provider for this listing's tour."
        busy={resyncListing.isPending}
        confirmLabel="Re-sync"
        onCancel={() => setRetryEid(null)}
        onConfirm={confirmRetry}
      />
    </div>
  );
};

export default SyncTab;
